import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { AuthService } from '../../../../core/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';

import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { CredentialRevealModalComponent } from '../../../../shared/components/credential-reveal-modal/credential-reveal-modal';

@Component({
  selector: 'app-user-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    PhoneInputComponent,
    AppButton,
    CredentialRevealModalComponent,
  ],
  templateUrl: './user-modal.html',
})
export class UserModalComponent implements OnInit {
  @Input() user: any = null;
  @Input() branchId!: number | null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  @Output() navigateToProfiles = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  userForm!: FormGroup;
  isEditMode = false;
  saving = false;
  showError = false;
  showNoProfileDialog = false;
  private _dialogClosing = false;

  // Shown right after a successful invite, unconditionally — email delivery
  // isn't reliable enough to assume the admin doesn't need this (see
  // denis.voicu@metasmile.com never receiving an Outlook invite). Closing it
  // is what actually completes the create flow (emits saved).
  showCredentialModal = false;
  createdEmail: string | null = null;
  createdTempPassword: string | null = null;

  accessProfiles: any[] = [];

  /** This modal only ever creates type-5 (Usuário) accounts; when editing, show the user's actual type. */
  get userTypeDisplay(): string {
    return this.user?.user_type_name || 'Usuário';
  }

  ngOnInit() {
    this.isEditMode = !!this.user;
    this.initForm();
    this.loadAccessProfiles();

    if (this.isEditMode) {
      this.patchFormValues();
    }
  }

  initForm() {
    this.userForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [null, Validators.required],
      // Required: a user saved with no access profile has no permissions at all
      // and lands on /no-permission, which reads as a broken account rather than
      // an unfinished one.
      profileId: [null, Validators.required],
    });
  }

  patchFormValues() {
    this.userForm.patchValue({
      name: this.user.name,
      email: this.user.email,
      phone: this.user.cell_phone,
      profileId: this.user.profile?.id || null,
    });

    if (this.isEditMode) {
      this.userForm.get('email')?.disable();
    }
  }

  async loadAccessProfiles() {
    try {
      // branchId matters: without it the RPC's p_branch_id is null, which returns
      // EVERY branch's custom profiles, not just this clinic's. This was the last
      // caller still missing the scoping argument that access-profiles-tab and
      // professionals.service already pass. The shared default roles
      // (branches_id IS NULL) are still included by the RPC, so the list is
      // this clinic's profiles plus the system ones.
      this.accessProfiles = await firstValueFrom(
        this.settingsService.getAccessProfiles('', this.branchId),
      );
      this.cdr.detectChanges();
    } catch (err) {
      console.error(err);
    }
  }

  onProfileDropdownClick(event: MouseEvent) {
    if (this._dialogClosing) return;
    this.showNoProfileDialog = true;
    this.cdr.detectChanges();
  }

  dismissNoProfileDialog() {
    this.showNoProfileDialog = false;
    this._dialogClosing = true;
    setTimeout(() => { this._dialogClosing = false; }, 300);
    this.cdr.detectChanges();
  }

  goToProfiles() {
    this.showNoProfileDialog = false;
    this.navigateToProfiles.emit();
    this.close.emit();
  }

  async saveUser() {
    if (this.userForm.invalid) {
      this.showError = true;
      return;
    }

    this.showError = false;
    this.saving = true;

    try {
      const formData = this.userForm.getRawValue();

      // Canonical E.164 straight from app-phone-input.
      const fullPhone = formData.phone || '';

      // Guard: a doctor role (Consultas:create) can only go to a Médico (type 3) user.
      const selectedProfile = this.accessProfiles.find((p) => p.id === formData.profileId);
      const targetUserType = this.user?.user_type_id ?? 5; // new users are always type 5
      if (selectedProfile?.requires_medic && targetUserType !== 3) {
        toast.error(this.translate.instant('settings_role_requires_medic'));
        this.saving = false;
        this.cdr.detectChanges();
        return;
      }

      // 1. Explicit email check before attempting to create
      if (!this.isEditMode) {
        const { exists } = await this.authService.checkEmailExists(formData.email);
        if (exists) {
          toast.error(this.translate.instant('ERROR'), {
            description: this.translate.instant('email_already_registered'),
          });
          this.saving = false;
          this.cdr.detectChanges();
          return;
        }
      }

      if (this.isEditMode) {
        await firstValueFrom(
          this.settingsService.updateUser(
            this.user.id,
            formData.name,
            formData.email,
            fullPhone,
            formData.profileId,
            this.branchId!
          ),
        );
        toast.success(this.translate.instant('settings_user_success_update'));
      } else {
        const created = await firstValueFrom(
          this.settingsService.createUser(
            formData.name,
            formData.email,
            fullPhone,
            this.branchId!,
            formData.profileId,
            5,
          ),
        );

        // createUser emails the new user a TEMPORARY PASSWORD (no link to click)
        // and forces them to replace it on first login. Say so, or the admin has
        // no idea what the person actually received.
        toast.success(this.translate.instant('settings_user_success_create'), {
          description: this.translate.instant('invite_temp_password_sent'),
        });

        // Show the password too, every time — not just when email fails.
        // saved/close only fire once this is dismissed (see onCredentialModalClosed).
        if (created?.tempPassword) {
          this.createdEmail = formData.email;
          this.createdTempPassword = created.tempPassword;
          this.showCredentialModal = true;
          this.saving = false;
          this.cdr.detectChanges();
          return;
        }
      }

      this.saved.emit();
    } catch (err: any) {
      console.error(err);
      const errorMsg = err?.error?.message || err?.message || '';
      if (errorMsg.includes('DOCTOR_ROLE_REQUIRES_MEDIC')) {
        toast.error(this.translate.instant('settings_role_requires_medic'));
      } else if (errorMsg.toLowerCase().includes('already registered') || errorMsg === 'email_already_registered') {
        toast.error(this.translate.instant('ERROR'), {
          description: this.translate.instant('email_already_registered'),
        });
      } else if (errorMsg === 'settings_user_create_forbidden') {
        toast.error(this.translate.instant('settings_user_create_forbidden'));
      } else {
        toast.error(
          this.translate.instant(this.isEditMode ? 'settings_user_error_update' : 'settings_user_error_create'),
        );
      }
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  onCredentialModalClosed() {
    this.showCredentialModal = false;
    this.createdEmail = null;
    this.createdTempPassword = null;
    this.saved.emit();
  }
}
